import { makeAutoObservable } from "mobx";
import { type Path } from "react-router-dom";
import { createAuthCallbackUrl } from "./authCallbackUrl";
import { type AuthSession, type AuthSessionStorage } from "./authSessionStorage";
import { type Authenticator } from "./authenticator";

type AuthStoreState = "unauthenticated" | "authenticating" | "authenticated";

export class AuthStore {
  private readonly _authenticator: Authenticator;
  private readonly _authSessionStorage: AuthSessionStorage;

  constructor(authenticator: Authenticator, authSessionStorage: AuthSessionStorage) {
    makeAutoObservable(this);
    this._authenticator = authenticator;
    this._authSessionStorage = authSessionStorage;
  }

  private _state: AuthStoreState = "unauthenticated";

  get state(): AuthStoreState {
    return this._state;
  }

  private set state(state: AuthStoreState) {
    this._state = state;
  }

  get isTokenRefreshed(): boolean {
    return this._authenticator.isTokenRefreshed;
  }

  get getCompanyIdFromToken(): string | undefined {
    return this._authenticator.getCompanyId();
  }

  initialize(): void {
    this._authSessionStorage.cleanup();
    this._state = this._authenticator.isAuthenticated ? "authenticated" : "unauthenticated";
  }

  getAuthUrl(callbackUrl: URL, state: string, loginHint?: string): string {
    return this._authenticator.getAuthUrl(callbackUrl, { state, loginHint });
  }

  /**
   * Starts a login that comes back to the fixed callback and then returns the user to `returnUrl`.
   */
  getReturnAuthUrl(returnUrl: URL): string {
    const state = this.startSession({
      pathname: returnUrl.pathname,
      search: returnUrl.search,
      hash: returnUrl.hash,
    });

    return this.getAuthUrl(createAuthCallbackUrl(returnUrl.origin), state);
  }

  startSession(entryPath: Path): string {
    return this._authSessionStorage.start(entryPath);
  }

  loadSession(sessionId: string): AuthSession | null {
    return this._authSessionStorage.load(sessionId);
  }

  removeSession(sessionId: string): void {
    this._authSessionStorage.remove(sessionId);
  }

  async authenticate(currentUrl: URL, callbackUrl: URL): Promise<void> {
    this.state = "authenticating";
    try {
      const authenticated = await this._authenticator.authenticate(currentUrl, callbackUrl);
      this.state = authenticated ? "authenticated" : "unauthenticated";
    } catch (e) {
      this.state = "unauthenticated";
      throw e;
    }
  }

  /**
   * In case we need to refresh token after company creation or smth important for security context
   */
  refresh(): void {
    this._authenticator.markTokenRefreshed();
    this.reset();
  }

  /**
   * Just the cleanup when the token is expired
   */
  reset(): void {
    this._authenticator.reset();
    this._state = "unauthenticated";
  }

  /** init token refreshed flag */
  initTokenRefreshedFlag(): void {
    this._authenticator.initTokenRefreshedFlag();
  }

  /**
   * When the customer explicitly wants to log out
   */
  async logout(onLogout?: (url: string) => void): Promise<void> {
    await this._authenticator.logout();
    if (onLogout) {
      onLogout(this._authenticator.getLogoutRedirectUrl());
    } else {
      this._state = "unauthenticated";
    }
  }
}
